import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Navbar from "../../navbar";
import Header from "../../header";
import { useSelector } from "react-redux";
import { getAllCertificates } from "../../../store/reducers/applicationSlice";
import { downloadCertificate } from "../../../store/reducers/fileSlice";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import Pagination from "../dashboard/appPagination";

const columnHelper = createColumnHelper();

const AppCertifications = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const [certificates, setCertificates] = useState([]);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [activeItemsPerPage, setActiveItemsPerPage] = useState(10);
  const [expiredCurrentPage, setExpiredCurrentPage] = useState(1);
  const [expiredItemsPerPage, setExpiredItemsPerPage] = useState(10);

  useEffect(() => {
    async function getCertificates() {
      setLoading(true);
      try {
        const response = await getAllCertificates({ authToken });
        const list = Array.isArray(response?.applications)
          ? response.applications
          : Array.isArray(response?.applications?.data)
            ? response.applications.data
            : [];
        setCertificates(list);
      } finally {
        setLoading(false);
      }
    }
    if (authToken) {
      getCertificates();
    }
  }, [authToken]);

  const formatDate = (inputDate) => {
    if (!inputDate) return "—";
    const date = new Date(inputDate);
    if (Number.isNaN(date.getTime())) return "—";
    const options = { year: "numeric", month: "long", day: "2-digit" };
    return date.toLocaleDateString(undefined, options);
  };

  const getExpiryDate = (item) =>
    item?.due_date || item?.expired_at || item?.expire_date;

  const hasDatePassed = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const filteredCertificates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return certificates || [];
    return (certificates || []).filter((item) =>
      item?.name?.toLowerCase()?.includes(query)
    );
  }, [certificates, searchQuery]);

  const { activeCertificates, expiredCertificates } = useMemo(() => {
    const active = [];
    const expired = [];
    filteredCertificates.forEach((item) => {
      if (hasDatePassed(getExpiryDate(item))) {
        expired.push(item);
      } else {
        active.push(item);
      }
    });
    return { activeCertificates: active, expiredCertificates: expired };
  }, [filteredCertificates]);

  const paginateData = (data, currentPage, itemsPerPage) => {
    const validItemsPerPage =
      typeof itemsPerPage === "number" && itemsPerPage > 0 ? itemsPerPage : 10;
    const startIndex = (currentPage - 1) * validItemsPerPage;
    return data.slice(startIndex, startIndex + validItemsPerPage);
  };

  const getTotalPages = (totalItems, itemsPerPage) => {
    const validItemsPerPage =
      typeof itemsPerPage === "number" && itemsPerPage > 0 ? itemsPerPage : 10;
    return Math.max(1, Math.ceil(totalItems / validItemsPerPage));
  };

  const paginatedActiveCertificates = useMemo(
    () => paginateData(activeCertificates, activeCurrentPage, activeItemsPerPage),
    [activeCertificates, activeCurrentPage, activeItemsPerPage]
  );

  const paginatedExpiredCertificates = useMemo(
    () =>
      paginateData(expiredCertificates, expiredCurrentPage, expiredItemsPerPage),
    [expiredCertificates, expiredCurrentPage, expiredItemsPerPage]
  );

  // Keep current page in range when filters/data shrink
  useEffect(() => {
    const totalPages = getTotalPages(activeCertificates.length, activeItemsPerPage);
    if (activeCurrentPage > totalPages) {
      setActiveCurrentPage(totalPages);
    }
  }, [activeCertificates.length, activeItemsPerPage, activeCurrentPage]);

  useEffect(() => {
    const totalPages = getTotalPages(
      expiredCertificates.length,
      expiredItemsPerPage
    );
    if (expiredCurrentPage > totalPages) {
      setExpiredCurrentPage(totalPages);
    }
  }, [expiredCertificates.length, expiredItemsPerPage, expiredCurrentPage]);

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
    setActiveCurrentPage(1);
    setExpiredCurrentPage(1);
  };

  const handleDownload = useCallback(
    (item) => {
      if (downloadLoader === item.id) return;
      downloadCertificate(item.id, authToken, setDownloadLoader);
    },
    [authToken, downloadLoader]
  );

  const buildColumns = useCallback(
    (currentPage, itemsPerPage) => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("name", {
        header: "Certificate Name",
        cell: (info) => (
          <strong style={{ whiteSpace: "nowrap" }} title={info.getValue() || ""}>
            {info.getValue() || "—"}
          </strong>
        ),
      }),
      columnHelper.display({
        id: "issue_date",
        header: "Issued",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDate(info.row.original?.issue_date)}
          </span>
        ),
      }),
      columnHelper.display({
        id: "due_date",
        header: "Expire",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }} className="due">
            {formatDate(getExpiryDate(info.row.original))}
          </span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const item = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id={`dropdownMenuButton-${item.id}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby={`dropdownMenuButton-${item.id}`}
                >
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownload(item);
                      }}
                    >
                      {downloadLoader === item.id
                        ? "Downloading..."
                        : "Download"}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadLoader, handleDownload]
  );

  const activeColumns = useMemo(
    () => buildColumns(activeCurrentPage, activeItemsPerPage),
    [buildColumns, activeCurrentPage, activeItemsPerPage]
  );

  const expiredColumns = useMemo(
    () => buildColumns(expiredCurrentPage, expiredItemsPerPage),
    [buildColumns, expiredCurrentPage, expiredItemsPerPage]
  );

  const activeTable = useReactTable({
    data: paginatedActiveCertificates,
    columns: activeColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const expiredTable = useReactTable({
    data: paginatedExpiredCertificates,
    columns: expiredColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderTable = (table, emptyMessage) => (
    <div className="boardContent">
      <div className="table-responsive">
        <table className="table">
          <thead className="boardTheadcompany">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} style={{ whiteSpace: "nowrap" }}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="text-center"
                  colSpan={table.getHeaderGroups()?.[0]?.headers.length || 5}
                >
                  <div className="d-flex justify-content-center my-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="boardTrowcompany">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ whiteSpace: "nowrap" }}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={table.getHeaderGroups()?.[0]?.headers.length || 5}
                  className="text-center"
                >
                  <h2 className="text-danger my-4 py-4">{emptyMessage}</h2>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <Navbar />
      <Header handleSearchInputChange={handleSearchInputChange} />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft mb-3">
              <h2>Certifications</h2>
            </div>
          </div>
          {renderTable(activeTable, "No Certificate Found")}
          {!loading && activeCertificates.length > 0 && (
            <Pagination
              currentPage={activeCurrentPage}
              setCurrentPage={setActiveCurrentPage}
              totalPages={getTotalPages(
                activeCertificates.length,
                activeItemsPerPage
              )}
              itemsPerPage={activeItemsPerPage}
              setItemsPerPage={setActiveItemsPerPage}
              onItemsPerPageChange={(value) => {
                setActiveItemsPerPage(value);
                setActiveCurrentPage(1);
              }}
              totalItems={activeCertificates.length}
            />
          )}
        </div>

        <div className="boxAppBoard boxWhite mt-4">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft mb-3">
              <h2>Expired</h2>
            </div>
          </div>
          {renderTable(expiredTable, "No Certificate Found")}
          {!loading && expiredCertificates.length > 0 && (
            <Pagination
              currentPage={expiredCurrentPage}
              setCurrentPage={setExpiredCurrentPage}
              totalPages={getTotalPages(
                expiredCertificates.length,
                expiredItemsPerPage
              )}
              itemsPerPage={expiredItemsPerPage}
              setItemsPerPage={setExpiredItemsPerPage}
              onItemsPerPageChange={(value) => {
                setExpiredItemsPerPage(value);
                setExpiredCurrentPage(1);
              }}
              totalItems={expiredCertificates.length}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppCertifications;
